import { Link } from 'react-router-dom'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { ChevronRight, X } from 'lucide-react'
import { useDecisions } from '@/hooks/useDecisions'
import { useComplianceAlerts } from '@/hooks/useComplianceAlerts'
import { useDismissedAlerts } from '@/hooks/useDismissedAlerts'
import { isPending, isFyi, urgencyVariant } from '@/lib/decision-utils'
import { riskRank, riskVariant, riskLabel } from '@/lib/alert-utils'
import { decisionContext } from '@/lib/decision-context'
import { timeAgo } from '@/lib/utils'
import type { Decision, ComplianceAlert } from '@/lib/api'

/** One-line preview of what a decision is actually about, so Mark can tell an
 *  outbound press email from a routine nudge without opening it. */
function previewOf(decision: Decision): string | null {
  const ctx = decisionContext(decision)
  const text = ctx.description ?? ctx.workProduct?.body ?? null
  if (!text) return null
  const flat = text.replace(/\s+/g, ' ').trim()
  return flat.length > 140 ? `${flat.slice(0, 140)}…` : flat
}

interface NeedsAttentionProps {
  /** When true, only critical/high severity items are shown. */
  criticalOnly: boolean
}

const URGENCY_RANK: Record<string, number> = { CRITICAL: 4, HIGH: 3, NORMAL: 2, LOW: 1 }

type Item =
  | { kind: 'decision'; rank: number; decision: Decision }
  | { kind: 'alert'; rank: number; alert: ComplianceAlert }

export function NeedsAttention({ criticalOnly }: NeedsAttentionProps) {
  const decisionsQ = useDecisions()
  const alertsQ = useComplianceAlerts()
  const { isDismissed, dismiss } = useDismissedAlerts()

  const isLoading = decisionsQ.isLoading || alertsQ.isLoading

  const items: Item[] = [
    // Actionable decisions only — FYI / informational items live in the queue's
    // Acknowledge section and never demand attention here.
    ...(decisionsQ.data?.data ?? [])
      .filter((d) => isPending(d) && !isFyi(d))
      .map<Item>((d) => ({
        kind: 'decision',
        rank: URGENCY_RANK[(d.urgency ?? '').toUpperCase()] ?? 2,
        decision: d,
      })),
    ...(alertsQ.data?.alerts ?? [])
      .filter((a) => !isDismissed(a.id))
      .map<Item>((a) => ({ kind: 'alert', rank: riskRank(a.risk_level), alert: a })),
  ]
    .filter((item) => (criticalOnly ? item.rank >= 3 : true))
    .sort((a, b) => b.rank - a.rank)

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle>NEEDS ATTENTION</CardTitle>
        <span className="font-mono text-[0.65rem] text-cmd-outline tabular-nums">
          {criticalOnly ? 'CRITICAL · ' : ''}
          {items.length}
        </span>
      </CardHeader>
      <CardContent className="space-y-0">
        {isLoading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 p-3">
              <Skeleton className="h-3 w-3/4" />
            </div>
          ))
        ) : items.length === 0 ? (
          <p className="font-label text-sm text-cmd-outline p-3">
            {criticalOnly ? 'NO CRITICAL ITEMS' : 'ALL CLEAR — nothing needs you right now'}
          </p>
        ) : (
          items.map((item, i) =>
            item.kind === 'decision' ? (
              <DecisionRow key={`d-${item.decision.id}`} decision={item.decision} index={i} />
            ) : (
              <AlertRow
                key={`a-${item.alert.id}`}
                alert={item.alert}
                index={i}
                onDismiss={() => dismiss([item.alert.id])}
              />
            ),
          )
        )}
      </CardContent>
    </Card>
  )
}

/**
 * A decision that needs Mark. Deliberately NOT actionable from here: approving
 * an outbound press email or customer message blind — with no content preview —
 * is exactly the risk this card used to carry. Instead it shows a one-line
 * preview and a REVIEW link to the full decision card, where accept/reject
 * consequences are spelled out.
 */
function DecisionRow({ decision, index }: { decision: Decision; index: number }) {
  const preview = previewOf(decision)
  return (
    <Link
      to={`/dashboard/decisions?focus=${encodeURIComponent(decision.id)}`}
      className={`group flex items-start gap-3 p-3 ${index % 2 === 0 ? 'bg-cmd-surface' : 'bg-cmd-low'} hover:bg-cmd-high`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <Badge variant={urgencyVariant(decision.urgency)} className="text-[9px] px-1 py-0">
            {(decision.urgency ?? 'NORMAL').toUpperCase()}
          </Badge>
          <span className="font-mono text-[0.6rem] text-cmd-outline">DECISION · {decision.skillName}</span>
        </div>
        <p className="text-sm text-white/90 mt-1 truncate">{decision.summary}</p>
        {preview && <p className="text-[0.75rem] text-white/60 mt-0.5 line-clamp-2">{preview}</p>}
        <p className="font-mono text-[0.6rem] text-cmd-outline mt-0.5">{timeAgo(decision.createdAt)}</p>
      </div>
      <span className="flex h-7 shrink-0 items-center border border-cmd-outline px-3 font-label text-[0.6rem] tracking-wider text-cmd-outline group-hover:text-white">
        REVIEW
        <ChevronRight className="ml-1 h-3 w-3" aria-hidden="true" />
      </span>
    </Link>
  )
}

function AlertRow({
  alert,
  index,
  onDismiss,
}: {
  alert: ComplianceAlert
  index: number
  onDismiss: () => void
}) {
  return (
    <div className={`flex items-start gap-3 p-3 ${index % 2 === 0 ? 'bg-cmd-surface' : 'bg-cmd-low'}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <Badge variant={riskVariant(alert.risk_level)} className="text-[9px] px-1 py-0">
            {riskLabel(alert.risk_level)}
          </Badge>
          <span className="font-mono text-[0.6rem] text-cmd-outline">
            ALERT · {alert.tenant_name}
          </span>
        </div>
        <p className="text-sm text-white/90 mt-1 truncate">{alert.description}</p>
        <p className="font-mono text-[0.6rem] text-cmd-outline mt-0.5">
          {alert.item_type.replace(/_/g, ' ')} · {alert.days_remaining}d remaining
        </p>
      </div>
      <Button
        size="icon-sm"
        variant="ghost"
        onClick={onDismiss}
        aria-label="Acknowledge alert"
        className="shrink-0"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </Button>
    </div>
  )
}
